<script setup lang="ts">
import { computed } from 'vue'
import type { RoleCode } from '@/types/admin'
import { ADMIN_ROLES } from '@/data/roles'
import { adapter } from '@/services/prototype-adapter'
import { useAuthStore } from '@/stores/auth'
import { useUiStore } from '@/stores/ui'
import AppDialog from '@/components/ui/AppDialog.vue'
import ProSelect from '@/components/pro/ProSelect.vue'
import AppIcon from '@/components/AppIcon.vue'

const auth = useAuthStore()
const ui = useUiStore()
const open = computed({
  get: () => ui.rolePreviewOpen,
  set: (value: boolean) => { ui.rolePreviewOpen = value },
})
const options = computed(() => [{ value: 'none', label: '按真实身份' }, ...ADMIN_ROLES.map(role => ({ value: role.code, label: role.name }))])
const selected = computed(() => auth.viewAsRole ?? 'none')
const selectedRole = computed(() => ADMIN_ROLES.find(role => role.code === auth.viewAsRole))
const permissionCount = computed(() => auth.permissions.length)

function change(value: string | number | boolean | string[]): void {
  const next = String(value ?? 'none')
  auth.switchViewAs(next === 'none' ? null : next as RoleCode)
  ui.toast('info', next === 'none' ? '已恢复真实身份视角' : `已切换到${auth.roleName}视角`, '仅改变当前原型的菜单与按钮可见性，不改变真实授权。')
}
</script>

<template>
  <AppDialog v-model="open" title="角色视角预览" subtitle="仅超级管理员可检查不同角色的菜单与按钮可见性" width="480px">
    <div class="role-preview">
      <div class="role-preview__current">
        <span class="role-preview__avatar"><AppIcon name="eye" :size="18" /></span>
        <div>
          <b>{{ auth.isViewingAs ? auth.roleName : '真实身份' }}</b>
          <small>{{ auth.profile?.name }} · 当前可见 {{ permissionCount }} 个权限点</small>
        </div>
        <span class="tag" :class="auth.isViewingAs ? 'tag--warning' : 'tag--success'">{{ auth.isViewingAs ? '预览中' : '主身份' }}</span>
      </div>
      <label class="role-preview__field">
        <span>预览角色</span>
        <ProSelect :model-value="selected" :options="options" :clearable="false" aria-label="预览角色" @update:model-value="change" />
      </label>
      <div v-if="selectedRole" class="role-preview__meta">
        <p>{{ selectedRole.description }}</p>
        <div><span>数据范围</span><b>{{ selectedRole.dataScopeLabel }}</b></div>
        <div><span>权限点</span><b>{{ adapter.permissionsForRole(selectedRole.code).length }} 项</b></div>
      </div>
      <p class="role-preview__note">预览只影响当前浏览器的菜单、路由和按钮展示；切换真实账号或刷新页面后仍以真实身份为准。</p>
    </div>
    <template #footer>
      <button type="button" class="btn" @click="open = false">关闭</button>
      <button type="button" class="btn" :disabled="!auth.isViewingAs" @click="change('none')">退出预览</button>
      <button type="button" class="btn btn--primary" @click="open = false">完成</button>
    </template>
  </AppDialog>
</template>

<style scoped>
.role-preview { display: grid; gap: 12px; }
.role-preview__current { display: flex; align-items: center; gap: 9px; padding: 10px; border-radius: 10px; background: var(--c-brand-mist); border: 1px solid #d9f0e6; }
.role-preview__avatar { width: 34px; height: 34px; border-radius: 9px; display: grid; place-items: center; background: #fff; color: var(--c-brand-strong); }
.role-preview__current > div { flex: 1; min-width: 0; display: flex; flex-direction: column; }
.role-preview__current b { font-size: 12px; color: var(--c-ink); }
.role-preview__current small { margin-top: 2px; font-size: 9.5px; color: var(--c-muted); }
.role-preview__field { display: grid; gap: 5px; font-size: 11px; color: var(--c-muted); }
.role-preview__meta { padding: 9px 10px; border-radius: 9px; background: var(--c-surface-muted); }
.role-preview__meta p { margin: 0 0 6px; font-size: 10px; line-height: 1.5; color: var(--c-muted); }
.role-preview__meta div { display: flex; gap: 8px; padding: 3px 0; font-size: 10px; }
.role-preview__meta span { color: var(--c-faint); }
.role-preview__meta b { color: var(--c-text); font-weight: 600; }
.role-preview__note { margin: 0; font-size: 9.5px; line-height: 1.55; color: var(--c-faint); }
</style>
